import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Tooltip, Input, Modal, message, Typography, Badge, Empty, Result,
  Space, Dropdown, Card, Row, Col, Segmented, Select, Spin, Collapse, Progress,
} from 'antd';
import { SearchOutlined, ReloadOutlined, CopyOutlined, ShopOutlined, ArrowRightOutlined, ArrowLeftOutlined, InfoCircleOutlined, CheckCircleFilled, CloseCircleFilled, ThunderboltOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getRecEmpleados,
  getRecEmpleadoById,
  updateRecEmpleadoEstado,
  procesarEmpleado,
  ESTADO_PENDIENTE,
  ESTADO_REALIZADO,
  ESTADO_DESCARTADO,
  INCIDENCIA_TOOLTIPS,
  INCIDENCIA_COLORS,
  type ReconciliacionEmpleado,
  type ProcesarEmpleadoResponse,
} from '../../api/reconciliacionApi';
import FarmaciaTitularesModal from '../FarmaciaTitularesModal';

const { Text } = Typography;
const { TextArea } = Input;

const CRM_CLIENT_URL = 'https://gedeco.farmaconsulting.es/FamiliasDefinibles_Ficha.aspx?id=';
const CRM_CLIENT_URL_PARAMS = '&idFam=25&idFamCls=59&pag=242&sraccess=1&tmp=758';
const CRM_PHARMACY_URL = 'https://gedeco.farmaconsulting.es/FamiliasDefinibles_Ficha.aspx?id=';
const CRM_PHARMACY_URL_PARAMS = '&idFam=3&idFamCls=15&pag=25&sraccess=1&tmp=616';

type BloqueoFilter = 'todos' | 'sin' | 'con';

const tipoLabel = (tipo: string) => tipo === 'AE' ? 'Alta' : tipo === 'CE' ? 'Cierre' : tipo;

interface EmpleadosTabProps {
  selectedRecIds?: number[];
}

const EmpleadosTab: React.FC<EmpleadosTabProps> = ({ selectedRecIds }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<ReconciliacionEmpleado[]>([]);
  const [filteredData, setFilteredData] = useState<ReconciliacionEmpleado[]>([]);
  const [selectedEstado, setSelectedEstado] = useState<number>(ESTADO_PENDIENTE);
  const [bloqueoFilter, setBloqueoFilter] = useState<BloqueoFilter>('todos');
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [processing, setProcessing] = useState(false);
  const [estadoCounts, setEstadoCounts] = useState({ pendiente: 0, realizada: 0, descartada: 0 });
  const [loadingCounts, setLoadingCounts] = useState(false);

  // Batch progress
  const [batchProgress, setBatchProgress] = useState<{
    total: number; current: number; currentName: string;
    success: number; failed: number; cancelled: boolean; done: boolean;
  } | null>(null);
  const [batchProcessingId, setBatchProcessingId] = useState<number | null>(null);
  const cancelBatchRef = React.useRef(false);

  // Modal detalle
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ReconciliacionEmpleado | null>(null);
  const [editNotas, setEditNotas] = useState('');
  const [editEstadoId, setEditEstadoId] = useState<number>(ESTADO_PENDIENTE);
  const [showTitularesModal, setShowTitularesModal] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [procesarResult, setProcesarResult] = useState<ProcesarEmpleadoResponse | null>(null);

  const tieneBloqueo = (emp: ReconciliacionEmpleado) => emp.resultado === -1;

  const ESTADO_TODOS = -1;

  const fetchByEstado = async (estadoId: number) => {
    const apiEstado = estadoId === ESTADO_TODOS ? undefined : estadoId;
    if (!selectedRecIds || selectedRecIds.length === 0) return getRecEmpleados(undefined, apiEstado);
    const results = await Promise.all(selectedRecIds.map(rid => getRecEmpleados(rid, apiEstado)));
    return results.flat();
  };

  const loadCounts = async () => {
    setLoadingCounts(true);
    try {
      const [pendientes, realizadas, descartadas] = await Promise.all([
        fetchByEstado(ESTADO_PENDIENTE),
        fetchByEstado(ESTADO_REALIZADO),
        fetchByEstado(ESTADO_DESCARTADO),
      ]);
      setEstadoCounts({ pendiente: pendientes.length, realizada: realizadas.length, descartada: descartadas.length });
    } catch { /* silently fail */ }
    finally { setLoadingCounts(false); }
  };

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await fetchByEstado(selectedEstado);
      setData(result);
      applyFilters(result, searchText, bloqueoFilter);
    } catch { setError(true); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadData();
    setSelectedRowKeys([]);
  }, [selectedEstado, selectedRecIds]);

  useEffect(() => {
    loadCounts();
  }, [selectedRecIds]);

  const applyFilters = (items: ReconciliacionEmpleado[], text: string, bFilter: BloqueoFilter) => {
    let filtered = items;
    if (bFilter === 'sin') filtered = filtered.filter(e => !tieneBloqueo(e));
    if (bFilter === 'con') filtered = filtered.filter(e => tieneBloqueo(e));
    if (text.trim()) {
      const lower = text.toLowerCase();
      filtered = filtered.filter(e =>
        (e.farmacia?._Name || '').toLowerCase().includes(lower) ||
        (e.farmaceutico?._Name || '').toLowerCase().includes(lower) ||
        e.tipoOperacion.toLowerCase().includes(lower) ||
        (e.notasGestion || '').toLowerCase().includes(lower)
      );
    }
    setFilteredData(filtered);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applyFilters(data, value, bloqueoFilter);
  };

  const handleBloqueoChange = (value: BloqueoFilter) => {
    setBloqueoFilter(value);
    setSelectedRowKeys([]);
    applyFilters(data, searchText, value);
  };

  const sinBloqueoPendientes = data.filter(e => e.estadoId === ESTADO_PENDIENTE && !tieneBloqueo(e));

  // --- Procesamiento por lotes ---
  const processBatch = async (items: ReconciliacionEmpleado[]) => {
    setProcessing(true);
    cancelBatchRef.current = false;
    setBatchProgress({ total: items.length, current: 0, currentName: '', success: 0, failed: 0, cancelled: false, done: false });

    let success = 0;
    let failed = 0;

    for (let i = 0; i < items.length; i++) {
      if (cancelBatchRef.current) {
        setBatchProgress(prev => prev ? { ...prev, cancelled: true, done: true } : null);
        break;
      }

      const item = items[i];
      const name = item.farmaceutico?._Name || `ID ${item.farmaceuticoId}`;
      setBatchProcessingId(item.id);
      setBatchProgress(prev => prev ? { ...prev, current: i + 0.5, currentName: name } : null);

      try {
        const result = await procesarEmpleado(item.id);
        if (result.error !== 0 || !result.resultado.procesado) { failed++; } else { success++; }
        try {
          const refreshed = await getRecEmpleadoById(item.id);
          setData(prev => prev.map(e => e.id === item.id ? refreshed : e));
          setFilteredData(prev => prev.map(e => e.id === item.id ? refreshed : e));
        } catch { /* keep going */ }
      } catch { failed++; }

      setBatchProgress(prev => prev ? { ...prev, current: i + 1, success, failed } : null);
    }

    setBatchProcessingId(null);
    setBatchProgress(prev => prev ? { ...prev, done: true, success, failed } : null);
    setProcessing(false);
    setSelectedRowKeys([]);
    loadCounts();
  };

  const handleCancelBatch = () => { cancelBatchRef.current = true; };
  const handleCloseBatchProgress = () => { setBatchProgress(null); };

  const [showConfirm, setShowConfirm] = useState<{ items: ReconciliacionEmpleado[]; text: string } | null>(null);

  const handleConfirmOk = () => {
    if (showConfirm) {
      const items = showConfirm.items;
      setShowConfirm(null);
      setTimeout(() => processBatch(items), 300);
    }
  };

  const handleProcesarTodos = () => {
    setShowConfirm({
      items: sinBloqueoPendientes,
      text: `Se actualizarán las relaciones de empleado de ${sinBloqueoPendientes.length} casos sin bloqueo. Esta acción no se puede deshacer. ¿Continuar?`,
    });
  };

  const handleProcesarSeleccionados = () => {
    const items = data.filter(e => selectedRowKeys.includes(e.id));
    setShowConfirm({
      items,
      text: `Se actualizarán las relaciones de empleado de ${items.length} casos seleccionados. ¿Continuar?`,
    });
  };

  // --- Detalle individual ---
  const handleUpdateEstado = async (id: number, estadoId: number, notasGestion?: string) => {
    try {
      await updateRecEmpleadoEstado(id, estadoId, notasGestion);
      message.success('Empleado actualizado correctamente');
      setModalVisible(false);
      loadData();
      loadCounts();
    } catch {
      message.error('Error al actualizar el empleado');
    }
  };

  const openDetail = async (item: ReconciliacionEmpleado) => {
    setSelectedItem(item);
    setEditNotas(item.notasGestion || '');
    setEditEstadoId(item.estadoId);
    setProcesarResult(null);
    setModalVisible(true);
  };

  const handleEstadoChange = (value: string | number) => {
    setSelectedEstado(Number(value));
    setSearchText('');
    setBloqueoFilter('todos');
    setSelectedRowKeys([]);
  };

  const handleRefresh = async () => {
    await Promise.all([loadData(), loadCounts()]);
  };

  // --- Columnas ---
  const columns: ColumnsType<ReconciliacionEmpleado> = [
    {
      title: 'Rec',
      dataIndex: 'reconciliacionId',
      key: 'rec',
      width: 45,
      render: (val: number) => <Text type="secondary" style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipoOperacion',
      key: 'tipo',
      width: 80,
      filters: [
        { text: 'Alta', value: 'AE' },
        { text: 'Cierre', value: 'CE' },
      ],
      onFilter: (value, record) => record.tipoOperacion === value,
      render: (val: string) => <Tag color={val === 'AE' ? 'green' : 'red'}>{tipoLabel(val)}</Tag>,
    },
    {
      title: 'Farmacéutico',
      key: 'farmaceutico',
      render: (_, r) => r.farmaceutico?._Name || `ID ${r.farmaceuticoId}`,
      sorter: (a, b) => (a.farmaceutico?._Name || '').localeCompare(b.farmaceutico?._Name || ''),
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Farmacia',
      key: 'farmacia',
      render: (_, r) => r.farmacia?._Name || `ID ${r.farmaciaId}`,
      sorter: (a, b) => (a.farmacia?._Name || '').localeCompare(b.farmacia?._Name || ''),
      ellipsis: true,
      width: 250,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 110,
      render: (val: string) => val ? new Date(val).toLocaleDateString('es-ES') : '-',
    },
    {
      title: <Tooltip title="Bloqueos que requieren supervisión">Bloqueos</Tooltip>,
      key: 'bloqueos',
      width: 100,
      render: (_, r) => {
        const tags: React.ReactNode[] = [];
        // Bloqueos de procesamiento — extraer tipo del prefijo [XX] en resultadoNotas
        if (r.resultado === -1 && r.resultadoNotas) {
          const match = r.resultadoNotas.match(/^\[([A-Z0-9]+)\]/);
          if (match) {
            tags.push(
              <Tooltip key={`proc-${match[1]}`} title={r.resultadoNotas.replace(/^\[[A-Z0-9]+\]\s*/, '')}>
                <Tag color="volcano">{match[1]}</Tag>
              </Tooltip>
            );
          }
        }
        return tags.length > 0 ? tags : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estadoId',
      key: 'estado',
      width: 100,
      render: (val: number, record: ReconciliacionEmpleado) => {
        if (batchProcessingId === record.id) {
          return <Spin size="small" />;
        }
        const estados: Record<number, { label: string; color: string }> = {
          [ESTADO_PENDIENTE]: { label: 'Pendiente', color: 'orange' },
          [ESTADO_REALIZADO]: { label: 'Realizada', color: 'green' },
          [ESTADO_DESCARTADO]: { label: 'Descartada', color: 'default' },
        };
        const est = estados[val] || { label: `Estado ${val}`, color: 'default' };
        return <Tag color={est.color}>{est.label}</Tag>;
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    getCheckboxProps: (record: ReconciliacionEmpleado) => ({
      disabled: tieneBloqueo(record) || record.estadoId !== ESTADO_PENDIENTE,
      title: tieneBloqueo(record) ? 'Los casos con bloqueo requieren revisión individual' : undefined,
    }),
  };

  if (error) {
    return (
      <Result
        status="error"
        title="Error al cargar empleados"
        extra={<Button type="primary" onClick={loadData} icon={<ReloadOutlined />}>Reintentar</Button>}
      />
    );
  }

  return (
    <div>
      <style>{`
        .batch-active-row > td {
          background-color: #e6f7ff !important;
        }
      `}</style>

      {/* Cabecera */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 16 }}>
        <Select
          value={bloqueoFilter}
          onChange={handleBloqueoChange}
          style={{ width: 180 }}
          size="large"
          options={[
            { label: 'Todos', value: 'todos' },
            { label: 'Sin bloqueo', value: 'sin' },
            { label: 'Con bloqueo', value: 'con' },
          ]}
        />
        {bloqueoFilter === 'sin' && (
          <>
            <Button
              type="primary"
              onClick={handleProcesarTodos}
              disabled={sinBloqueoPendientes.length === 0 || processing}
              loading={processing}
            >
              Procesar todos sin bloqueo ({sinBloqueoPendientes.length})
            </Button>
            <Button
              onClick={handleProcesarSeleccionados}
              disabled={selectedRowKeys.length === 0 || processing}
              loading={processing}
            >
              Procesar seleccionados ({selectedRowKeys.length})
            </Button>
          </>
        )}
        <div style={{ flex: 1 }} />
        <Input
          placeholder="Buscar empleado o farmacia..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Segmented
          value={selectedEstado}
          onChange={handleEstadoChange}
          disabled={loading || loadingCounts}
          options={[
            {
              label: (<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span>Todos</span><Badge count={estadoCounts.pendiente + estadoCounts.realizada + estadoCounts.descartada} style={{ backgroundColor: '#595959' }} showZero overflowCount={9999} /></div>),
              value: ESTADO_TODOS,
            },
            {
              label: (<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span>Pendiente</span><Badge count={estadoCounts.pendiente} style={{ backgroundColor: '#fa8c16' }} showZero overflowCount={9999} /></div>),
              value: ESTADO_PENDIENTE,
            },
            {
              label: (<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span>Realizada</span><Badge count={estadoCounts.realizada} style={{ backgroundColor: '#52c41a' }} showZero overflowCount={9999} /></div>),
              value: ESTADO_REALIZADO,
            },
            {
              label: (<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span>Descartada</span><Badge count={estadoCounts.descartada} style={{ backgroundColor: '#8c8c8c' }} showZero overflowCount={9999} /></div>),
              value: ESTADO_DESCARTADO,
            },
          ]}
          size="large"
        />
        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>Actualizar</Button>
      </div>

      {/* Barra de progreso del procesamiento por lotes */}
      {batchProgress && (
        <div style={{
          marginBottom: 12, padding: '12px 16px',
          background: batchProgress.done ? (batchProgress.failed > 0 ? '#fff7e6' : '#f6ffed') : '#e6f7ff',
          border: `1px solid ${batchProgress.done ? (batchProgress.failed > 0 ? '#ffd591' : '#b7eb8f') : '#91d5ff'}`,
          borderRadius: 6,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Space>
              {!batchProgress.done && <Spin size="small" />}
              <Text strong>
                {batchProgress.done
                  ? (batchProgress.cancelled
                    ? `Procesamiento cancelado — ${batchProgress.current - 1} de ${batchProgress.total}`
                    : `Procesamiento completado — ${batchProgress.total} empleados`)
                  : `Procesando ${Math.ceil(batchProgress.current)} de ${batchProgress.total}...`}
              </Text>
            </Space>
            {batchProgress.done ? (
              <Button size="small" onClick={handleCloseBatchProgress}>Cerrar</Button>
            ) : (
              <Button size="small" icon={<StopOutlined />} onClick={handleCancelBatch} danger>Cancelar</Button>
            )}
          </div>
          <Progress
            percent={Math.round((batchProgress.current / batchProgress.total) * 100)}
            status={batchProgress.done ? (batchProgress.failed > 0 ? 'exception' : 'success') : 'active'}
            size="small"
          />
          <div style={{ marginTop: 4, display: 'flex', gap: 16 }}>
            {!batchProgress.done && <Text type="secondary" style={{ fontSize: 12 }}>{batchProgress.currentName}</Text>}
            <Text type="success" style={{ fontSize: 12 }}>{batchProgress.success} OK</Text>
            {batchProgress.failed > 0 && <Text type="danger" style={{ fontSize: 12 }}>{batchProgress.failed} con error</Text>}
          </div>
        </div>
      )}

      {/* Tabla */}
      <Table
        dataSource={filteredData.map(e => ({ ...e, key: e.id }))}
        columns={columns}
        rowSelection={bloqueoFilter === 'sin' && !processing ? rowSelection : undefined}
        loading={loading}
        pagination={processing ? false : { defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: total => `${total} registros` }}
        rowClassName={(record) => batchProcessingId === record.id ? 'batch-active-row' : ''}
        onRow={record => ({
          onClick: processing ? undefined : () => openDetail(record),
          style: {
            cursor: processing ? 'default' : 'pointer',
          },
        })}
        size="middle"
        scroll={{ x: 900 }}
        locale={{ emptyText: <Empty description="No hay empleados pendientes" /> }}
      />

      {/* Modal de detalle */}
      {modalVisible && <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        title={selectedItem ? <span><Tag color={selectedItem.tipoOperacion === 'AE' ? 'green' : 'red'}>{tipoLabel(selectedItem.tipoOperacion)}</Tag> {selectedItem.farmaceutico?._Name || 'ID ' + selectedItem.farmaceuticoId}</span> : ''}
        width="60%"
        style={{ top: 20 }}
        styles={{ body: { padding: '24px', background: '#fafafa' } }}
      >
        {selectedItem && (
          <div>
            {/* Layout: Farmacia (izq) + flecha + Empleado (der) */}
            <Row gutter={[0, 16]} style={{ marginBottom: 24 }}>
              {/* Card Farmacia */}
              <Col xs={24} lg={11}>
                <Card
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Dropdown
                        menu={{
                          items: [{
                            key: 'gs',
                            label: <span>ID Gescom: <strong>{selectedItem.farmaciaId}</strong> <CopyOutlined style={{ color: '#999', marginLeft: 4 }} /></span>,
                            onClick: () => { navigator.clipboard.writeText(String(selectedItem.farmaciaId)); },
                          }],
                        }}
                        trigger={['click']}
                      >
                        <span style={{ cursor: 'pointer' }}>
                          <ShopOutlined style={{ marginRight: 8 }} />
                          Farmacia
                        </span>
                      </Dropdown>
                      <Tooltip title="Ver empleados actuales en Gescom">
                        <InfoCircleOutlined
                          style={{ color: '#1890ff', cursor: 'pointer', fontSize: 16 }}
                          onClick={() => setShowTitularesModal(true)}
                        />
                      </Tooltip>
                    </div>
                  }
                  bordered
                  style={{
                    borderColor: '#d9d9d9',
                    background: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    height: '100%',
                  }}
                  headStyle={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}
                >
                  <a
                    href={`${CRM_PHARMACY_URL}${selectedItem.farmaciaId}${CRM_PHARMACY_URL_PARAMS}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'none', fontWeight: 'bold', fontSize: 14, display: 'block' }}
                  >
                    {selectedItem.farmacia?._Name || `ID ${selectedItem.farmaciaId}`}
                  </a>
                  <div style={{
                    marginTop: 12,
                    padding: '4px 12px',
                    backgroundColor: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    borderRadius: 4,
                    fontSize: '13px',
                    color: '#0050b3',
                    display: 'inline-block',
                  }}>
                    {selectedItem.farmacia?.provincia || '—'}
                  </div>
                </Card>
              </Col>

              {/* Flecha + Card empleado */}
              <Col xs={24} lg={13}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingLeft: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selectedItem.tipoOperacion === 'AE' ? (
                      <Tooltip title="Entra en la farmacia">
                        <ArrowLeftOutlined style={{ fontSize: 18, color: '#389e0d', flexShrink: 0 }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Sale de la farmacia">
                        <ArrowRightOutlined style={{ fontSize: 18, color: '#cf1322', flexShrink: 0 }} />
                      </Tooltip>
                    )}
                    <Card
                      title={selectedItem.tipoOperacion === 'AE' ? 'Empleado entrante' : 'Empleado saliente'}
                      size="small"
                      bordered
                      style={{
                        flex: 1,
                        borderColor: selectedItem.tipoOperacion === 'AE' ? '#b7eb8f' : '#ffa39e',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      }}
                      headStyle={{
                        backgroundColor: selectedItem.tipoOperacion === 'AE' ? '#f6ffed' : '#fff1f0',
                        fontWeight: 'bold',
                        color: selectedItem.tipoOperacion === 'AE' ? '#389e0d' : '#cf1322',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                        <Space size="small">
                          {selectedItem.resultado != null && (selectedItem.resultado === 1
                            ? <CheckCircleFilled style={{ color: '#52c41a' }} />
                            : <CloseCircleFilled style={{ color: '#f5222d' }} />)}
                          <a
                            href={selectedItem.farmaceutico ? `${CRM_CLIENT_URL}${selectedItem.farmaceutico.id}${CRM_CLIENT_URL_PARAMS}` : undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', textDecoration: 'none' }}
                          >
                            {selectedItem.farmaceutico?._Name || `ID ${selectedItem.farmaceuticoId}`}
                          </a>
                        </Space>
                        {selectedItem.fecha && (
                          <Text type="secondary" style={{ fontSize: 12 }}>{new Date(selectedItem.fecha).toLocaleDateString('es-ES')}</Text>
                        )}
                      </div>
                    </Card>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Comentario (izq) + Bloqueos (der) */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} lg={11}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Comentario</Text>
                <TextArea
                  value={editNotas}
                  onChange={e => setEditNotas(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  placeholder="Añadir comentario..."
                />
              </Col>

              {tieneBloqueo(selectedItem) && (
                <Col xs={24} lg={13}>
                  <div style={{ paddingLeft: 8 }}>
                    <Card size="small" bordered style={{ borderColor: '#ffa39e', background: '#fff2f0' }}>
                      <div style={{ display: 'flex', gap: 12 }}>
                        {(() => {
                          const match = selectedItem.resultadoNotas?.match(/^\[([A-Z0-9]+)\]/);
                          return match ? <Tag color="red" style={{ flexShrink: 0, height: 'fit-content', fontSize: 14, padding: '2px 10px' }}>{match[1]}</Tag> : null;
                        })()}
                        <Text strong style={{ color: '#cf1322' }}>
                          {selectedItem.resultadoNotas?.replace(/^\[[A-Z0-9]+\]\s*/, '') || 'Caso con bloqueo'}
                        </Text>
                      </div>
                    </Card>
                  </div>
                </Col>
              )}
            </Row>

            {/* Panel de resultado de procesamiento */}
            {(procesarResult || selectedItem.notasProcesamiento) && (
              <Collapse
                size="small"
                defaultActiveKey={procesarResult ? ['1'] : []}
                style={{ marginBottom: 16 }}
                items={[{
                  key: '1',
                  label: <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span><ThunderboltOutlined style={{ marginRight: 8 }} />Resultado del procesamiento</span>{selectedItem.fechaProcesamiento && <Text type="secondary" style={{ fontSize: 12 }}>{new Date(selectedItem.fechaProcesamiento).toLocaleDateString('es-ES')}</Text>}</div>,
                  children: (
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        {procesarResult?.tipoIncidencia && <Tag color="red" style={{ marginRight: 8 }}>{procesarResult.tipoIncidencia}</Tag>}
                        <Text strong>
                          {selectedItem.notasProcesamiento || (procesarResult ? `Caso: ${procesarResult.resultadoCasoLimpio}` : '')}
                        </Text>
                      </div>
                      {selectedItem.resultado != null && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                          {selectedItem.resultado === 1
                            ? <CheckCircleFilled style={{ color: '#52c41a', marginTop: 4 }} />
                            : <CloseCircleFilled style={{ color: '#f5222d', marginTop: 4 }} />}
                          <div>
                            <Text>{selectedItem.farmaceutico?._Name || `ID ${selectedItem.farmaceuticoId}`}</Text>
                            {selectedItem.resultadoNotas && <Text type="secondary" style={{ display: 'block', fontSize: 12, whiteSpace: 'pre-wrap' }}>{selectedItem.resultadoNotas}</Text>}
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                }]}
              />
            )}

            {/* Acciones: estado selector + Procesar + Guardar */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select
                value={editEstadoId}
                onChange={setEditEstadoId}
                style={{ width: 160 }}
              >
                <Select.Option value={ESTADO_PENDIENTE}><span style={{ color: '#fa8c16' }}>● Pendiente</span></Select.Option>
                <Select.Option value={ESTADO_REALIZADO}><span style={{ color: '#52c41a' }}>● Realizada</span></Select.Option>
                <Select.Option value={ESTADO_DESCARTADO}><span style={{ color: '#8c8c8c' }}>● Descartada</span></Select.Option>
              </Select>

              <Button
                icon={<SaveOutlined />}
                onClick={() => handleUpdateEstado(selectedItem.id, editEstadoId, editNotas || undefined)}
              >
                Guardar
              </Button>

              {selectedItem.estadoId === ESTADO_PENDIENTE && (
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  loading={procesando}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Actualizar Gescom',
                      content: `Se actualizará la relación de empleado (${tipoLabel(selectedItem.tipoOperacion).toLowerCase()}) en Gescom.`,
                      okText: 'Aceptar',
                      cancelText: 'Cancelar',
                      onOk: async () => {
                        setProcesando(true);
                        try {
                          const result = await procesarEmpleado(selectedItem.id);
                          setProcesarResult(result);

                          if (result.error !== 0) {
                            message.error(`Error: ${result.mensajeError}`);
                          } else if (!result.resultado.procesado) {
                            message.warning(`Caso no limpio: ${result.resultadoCasoLimpio}`);
                          } else {
                            message.success('Empleado procesado correctamente');
                            setEditEstadoId(ESTADO_REALIZADO);
                          }

                          const refreshed = await getRecEmpleadoById(selectedItem.id);
                          setSelectedItem(refreshed);
                          setEditEstadoId(refreshed.estadoId);
                        } catch {
                          message.error('Error al comunicar con Gescom');
                        } finally {
                          setProcesando(false);
                        }
                      },
                    });
                  }}
                >
                  Procesar...
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>}

      {/* Modal confirmación procesamiento por lotes */}
      <Modal
        open={!!showConfirm}
        title="Actualizar Gescom"
        okText="Aceptar"
        cancelText="Cancelar"
        onOk={handleConfirmOk}
        onCancel={() => setShowConfirm(null)}
      >
        <p>{showConfirm?.text}</p>
      </Modal>


      {/* Modal titulares/empleados actuales en Gescom */}
      {selectedItem && (
        <FarmaciaTitularesModal
          visible={showTitularesModal}
          onClose={() => setShowTitularesModal(false)}
          farmaciaId={selectedItem.farmaciaId}
          farmaciaName={selectedItem.farmacia?._Name || 'Sin datos'}
          userId={1}
          modo="empleados"
        />
      )}
    </div>
  );
};

export default EmpleadosTab;
